import { zodResolver } from "@hookform/resolvers/zod"
import { format } from "date-fns"
import type { LucideIcon } from "lucide-react"
import {
  ArrowUpCircleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  FilterIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  SearchIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
  StoreIcon,
  UserIcon,
  XCircleIcon,
} from "lucide-react"
import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { MultiSelectOption } from "@/components/commons"
import {
  AppDateRangePicker,
  AppMultiSelectFilter,
  FilterChip,
} from "@/components/commons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  PaymentMethodNameEnum,
  SalesChannelEnum,
  StatusTrackingEnum,
} from "@/enums"
import { formatDocument } from "@/helpers/format"
import { useOrdersSearchParams } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { hasExactIdentifier } from "@/lib/exact-search"
import { cn } from "@/lib/utils"
import type {
  FilterOrdersSchemaProps,
  StatusFilterMode,
} from "@/schemas/filter-order"
import type { DateFieldFilter } from "@/schemas/filter-order"
import {
  DATE_FIELDS,
  filterOrdersSchema,
} from "@/schemas/filter-order"
import { DateHelper } from "@/utils"

const API_DATE_FORMAT = "yyyy-MM-dd"

interface ActiveFilter {
  /** Identifica o filtro para limpar e para rotular o chip. */
  key: string
  label: string
  value: string
}

/** O par de parâmetros que cada campo de data manda para a consulta. */
type DateParamName = keyof Pick<
  OrdersSearchParamsType,
  | "from"
  | "to"
  | "paidFrom"
  | "paidTo"
  | "expiresFrom"
  | "expiresTo"
  | "canceledFrom"
  | "canceledTo"
>

const DATE_FIELD_PARAMS: Record<
  DateFieldFilter,
  readonly [DateParamName, DateParamName]
> = {
  created: ["from", "to"],
  paid: ["paidFrom", "paidTo"],
  expires: ["expiresFrom", "expiresTo"],
  canceled: ["canceledFrom", "canceledTo"],
}

/**
 * Qual data a URL está recortando.
 *
 * <p>A escolha do operador não viaja num parâmetro próprio: ela está implícita em QUAL par de
 * datas veio. Ler isso de volta é o que faz o alternador reabrir na opção escolhida quando a
 * pessoa volta pelo histórico ou compartilha o link.</p>
 */
const dateFieldFromParams = (
  params?: OrdersSearchParamsType
): DateFieldFilter => {
  const chosen = DATE_FIELDS.find((field) => {
    const [start] = DATE_FIELD_PARAMS[field]
    return field !== "created" && !!params?.[start]
  })

  return chosen ?? "created"
}

const statusIconMap: Record<
  StatusTrackingEnum,
  LucideIcon
> = {
  [StatusTrackingEnum.PENDING]: ArrowUpCircleIcon,
  [StatusTrackingEnum.FUTURE]: ArrowUpCircleIcon,
  [StatusTrackingEnum.PRE_AUTHORIZATION_WAITING]:
    RefreshCwIcon,
  [StatusTrackingEnum.PRE_AUTHORIZATION_ERROR]: XCircleIcon,
  [StatusTrackingEnum.PRE_AUTHORIZATION_TIMEOUT_ERROR]:
    XCircleIcon,
  [StatusTrackingEnum.PRE_AUTHORIZED_BY_GATEWAY]:
    CheckCircleIcon,
  [StatusTrackingEnum.WAITING_PAYMENT]: ArrowUpCircleIcon,
  [StatusTrackingEnum.WAITING_PARTIAL_PAYMENT]:
    ArrowUpCircleIcon,
  [StatusTrackingEnum.WAITING_PAYMENT_PIX]:
    ArrowUpCircleIcon,
  [StatusTrackingEnum.WAITING_PAYMENT_BANK_SLIP]:
    ArrowUpCircleIcon,
  [StatusTrackingEnum.WAITING_PAYMENT_BOLEPIX]:
    ArrowUpCircleIcon,
  [StatusTrackingEnum.WAITING_PAYMENT_DIRECT_DEBIT]:
    ArrowUpCircleIcon,
  [StatusTrackingEnum.WAITING_PAYMENT_DIRECT_DEBIT_SCHEDULED]:
    ArrowUpCircleIcon,
  [StatusTrackingEnum.AUTHORIZATION_TIMEOUT_ERROR]:
    XCircleIcon,
  [StatusTrackingEnum.PAYMENT_IDENTIFIED]: CheckCircleIcon,
  [StatusTrackingEnum.CONFIRMED]: CheckCircleIcon,
  [StatusTrackingEnum.CANCELED_BY_GATEWAY]: XCircleIcon,
  [StatusTrackingEnum.CANCELED_BY_STORE]: XCircleIcon,
  [StatusTrackingEnum.CANCELED_BY_CUSTOMER]: XCircleIcon,
  [StatusTrackingEnum.EXPIRED]: XCircleIcon,
  [StatusTrackingEnum.SUBMITTED_FOR_REVERSED]:
    RefreshCwIcon,
  [StatusTrackingEnum.REVERSED]: XCircleIcon,
  [StatusTrackingEnum.REVERSED_FAILED]: XCircleIcon,
  [StatusTrackingEnum.SUBMITTED_FOR_REFUND]: RefreshCwIcon,
  [StatusTrackingEnum.REFUNDED]: CheckCircleIcon,
  [StatusTrackingEnum.PARTIAL_REFUNDED]: CheckCircleIcon,
  [StatusTrackingEnum.REFUND_FAILED]: XCircleIcon,
  [StatusTrackingEnum.REFUND_PENDING]: RefreshCwIcon,
  [StatusTrackingEnum.REFUND_CANCELED]: XCircleIcon,
  [StatusTrackingEnum.ERROR]: XCircleIcon,
}

const salesChannelIconMap: Record<
  SalesChannelEnum,
  LucideIcon
> = {
  [SalesChannelEnum.INSURANCE]: ShieldCheckIcon,
  [SalesChannelEnum.SUBSCRIPTION]: RefreshCwIcon,
  [SalesChannelEnum.VTEX_PLATFORM]: StoreIcon,
  [SalesChannelEnum.VTEX_SUBSCRIPTION]: RefreshCwIcon,
  [SalesChannelEnum.POS]: StoreIcon,
  [SalesChannelEnum.ANDROID_APP]: SmartphoneIcon,
  [SalesChannelEnum.ON_LINE]: CircleIcon,
  [SalesChannelEnum.ADMIN]: UserIcon,
}

// Os mais pedidos primeiro; o resto segue a ordem do enum.
const PRIORITY_STATUSES = [
  StatusTrackingEnum.PENDING,
  StatusTrackingEnum.CONFIRMED,
  StatusTrackingEnum.CANCELED_BY_STORE,
  StatusTrackingEnum.REFUNDED,
  StatusTrackingEnum.PARTIAL_REFUNDED,
  StatusTrackingEnum.PAYMENT_IDENTIFIED,
]

const ORDERED_STATUSES = [
  ...PRIORITY_STATUSES,
  ...Object.values(StatusTrackingEnum).filter(
    (status) => !PRIORITY_STATUSES.includes(status)
  ),
]

const csvToList = (value?: string) =>
  value ? value.split(",").filter(Boolean) : []

interface SalesChannelOption {
  value: string
  label: string
  icon: LucideIcon
}

export const OrdersFilterForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { params, startDate, endDate } =
    useOrdersSearchParams()

  const salesChannelOptions: SalesChannelOption[] = [
    {
      value: "",
      label: t("orders.filters.allChannels"),
      icon: CircleIcon,
    },
    ...Object.values(SalesChannelEnum).map((channel) => ({
      value: channel,
      label: t(`orders.salesChannels.${channel}`),
      icon: salesChannelIconMap[channel],
    })),
  ]

  const statusOptions: MultiSelectOption[] =
    ORDERED_STATUSES.map((status) => ({
      value: status,
      label: t(`orders.status.${status}`),
      icon: statusIconMap[status],
    }))

  const paymentMethodOptions: MultiSelectOption[] =
    Object.values(PaymentMethodNameEnum).map((method) => ({
      value: method,
      label: t(`common.paymentMethods.${method}`),
    }))

  const [salesChannelSelected, setSalesChannelSelected] =
    useState<SalesChannelOption | undefined>(
      salesChannelOptions.find(
        (channel) =>
          channel.value === (params?.salesChannel || "")
      )
    )
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  // O intervalo é um só: qual data ele recorta decide de onde ler o valor de volta.
  const [rangeStartParam, rangeEndParam] =
    DATE_FIELD_PARAMS[dateFieldFromParams(params)]

  // statusNot na URL indica que o operador salvou um recorte por exclusão.
  const defaultValues: FilterOrdersSchemaProps = {
    from: new Date(
      DateHelper.formatToISO(
        params?.[rangeStartParam] ?? DateHelper.currentDate()
      )
    ),
    to: new Date(
      DateHelper.formatToISO(
        params?.[rangeEndParam] ?? DateHelper.currentDate()
      )
    ),
    statuses: params?.statusNot
      ? csvToList(params.statusNot)
      : csvToList(params?.status),
    statusMode: params?.statusNot ? "exclude" : "include",
    paymentMethods: csvToList(params?.paymentMethod),
    customerDocument: params?.customerDocument,
    code: params?.code,
    ownId: params?.ownId,
    salesChannel: params?.salesChannel,
    dateField: dateFieldFromParams(params),
    includePeriodWithIdentifier: false,
  }

  const ordersSchema = useMemo(
    () => filterOrdersSchema(t),
    [t]
  )
  const form = useForm<FilterOrdersSchemaProps>({
    resolver: zodResolver(ordersSchema),
    defaultValues,
  })

  // eslint-disable-next-line react-hooks/incompatible-library
  const statusMode = form.watch("statusMode")
  const selectedToDate = form.watch("to")
  const selectedDateField = form.watch("dateField")
  // Identificador exato dispensa o período — e deixá-lo editável e preenchido faria parecer
  // filtro ativo. O campo se desliga e diz o porquê, em vez de ser silenciosamente ignorado.
  const searchesByIdentifier = hasExactIdentifier({
    customerDocument: form.watch("customerDocument"),
    code: form.watch("code"),
    ownId: form.watch("ownId"),
  })

  /**
   * O "Limpar" só aparece quando há o que limpar — e recorte de data também é filtro.
   *
   * <p>Presença não serve de teste aqui: `from`/`to` chegam preenchidos com hoje mesmo sem
   * ninguém escolher nada, então o botão ficaria sempre visível. O que conta é a busca DIFERIR do
   * estado inicial — outro período, ou o intervalo aplicado a outra data que não a de criação.</p>
   */
  const hasFilter = useMemo(() => {
    const today = DateHelper.currentDate()
    const filtersAnotherDateField =
      dateFieldFromParams(params) !== "created"
    const narrowsCreationPeriod =
      (!!params?.from && params.from !== today) ||
      (!!params?.to && params.to !== today)

    return (
      !!params?.code ||
      !!params?.customerDocument ||
      !!params?.ownId ||
      !!params?.status ||
      !!params?.statusNot ||
      !!params?.paymentMethod ||
      !!params?.salesChannel ||
      filtersAnotherDateField ||
      narrowsCreationPeriod
    )
  }, [params])

  const watched = form.watch()
  const includesPeriodWithIdentifier =
    watched.includePeriodWithIdentifier === true

  /**
   * O que está escolhido, filtro a filtro — é o que cada chip mostra fechado.
   *
   * <p>O recorte de data só conta como filtro quando difere do padrão (hoje, por criação):
   * senão a barra abriria já anunciando um filtro que ninguém escolheu.</p>
   */
  const activeFilters: ActiveFilter[] = useMemo(() => {
    const today = DateHelper.currentDate()
    const filters: ActiveFilter[] = []

    const texts: [string, string, string?][] = [
      ["customerDocument", t("orders.filters.customerDocument"), watched.customerDocument],
      ["code", t("orders.filters.orderCode"), watched.code],
      ["ownId", t("orders.filters.ownId"), watched.ownId],
    ]
    texts.forEach(([key, label, value]) => {
      if (value) filters.push({ key, label, value })
    })

    if (watched.salesChannel) {
      filters.push({
        key: "salesChannel",
        label: t("orders.filters.salesChannelLabel"),
        value: salesChannelSelected?.label ?? watched.salesChannel,
      })
    }

    if (watched.statuses?.length) {
      filters.push({
        key: "statuses",
        label:
          watched.statusMode === "exclude"
            ? t("orders.filters.statusNotLabel")
            : t("orders.filters.statusLabel"),
        value: watched.statuses
          .map((status) => t(`orders.status.${status}`))
          .join(", "),
      })
    }

    if (watched.paymentMethods?.length) {
      filters.push({
        key: "paymentMethods",
        label: t("orders.filters.paymentMethodLabel"),
        value: watched.paymentMethods
          .map((method) => t(`common.paymentMethods.${method}`))
          .join(", "),
      })
    }

    const bothDates = watched.from && watched.to
    const isDefaultRange =
      watched.dateField === "created" &&
      [watched.from, watched.to].every(
        (date) =>
          date && format(date as Date, API_DATE_FORMAT) === today
      )

    if (bothDates && !isDefaultRange) {
      filters.push({
        key: "dateRange",
        label: t(`orders.filters.${watched.dateField}Period`),
        value: `${format(watched.from as Date, "dd/MM/yy")} – ${format(
          watched.to as Date,
          "dd/MM/yy"
        )}`,
      })
    }

    return filters
  }, [watched, salesChannelSelected, t])

  const valueOf = (key: string) =>
    activeFilters.find((filter) => filter.key === key)?.value

  /**
   * O chip de data nunca fica mudo: sem recorte próprio, mostra o padrão da tela.
   *
   * <p>O rótulo dele é o campo escolhido ("Pago em", "Expira em"), não um "Data" genérico —
   * escolher o campo e o chip não mudar faz parecer que a escolha não pegou.</p>
   */
  const dateChipValue =
    valueOf("dateRange") ?? t("orders.filters.today")

  const clearFilter = (key: string) => {
    if (key === "dateRange") {
      form.setValue("dateField", "created")
      form.setValue("from", startDate)
      form.setValue("to", endDate)
      return
    }
    if (key === "statuses" || key === "paymentMethods") {
      form.setValue(key, [])
      return
    }
    if (key === "salesChannel") {
      form.setValue("salesChannel", undefined)
      setSalesChannelSelected(salesChannelOptions[0])
      return
    }
    form.setValue(
      key as "code" | "ownId" | "customerDocument",
      ""
    )
  }

  const handleSubmit = form.handleSubmit((data) => {
    // Busca por identificador exato ignora o período: quem tem o CPF, o código ou o próprio
    // identificador quer AQUELE pedido e não sabe de que data ele é. Exigir a janela obrigaria
    // a adivinhar a semana certa antes de encontrar.
    // O período sai da consulta na busca por identificador — a menos que tenha sido pedido.
    const appliesPeriod =
      !hasExactIdentifier(data) ||
      data.includePeriodWithIdentifier

    if (appliesPeriod && data.from > data.to) {
      toast({
        title: t("validation.startDateAfterEndDate"),
        variant: "destructive",
      })
      return
    }

    const searchParams = new URLSearchParams()
    const currentWhitelabelId = new URLSearchParams(
      window.location.search
    ).get("whiteLabelId")
    if (currentWhitelabelId) {
      searchParams.set("whiteLabelId", currentWhitelabelId)
    }

    const textFilters = {
      customerDocument: data.customerDocument,
      code: data.code,
      ownId: data.ownId,
      salesChannel: data.salesChannel,
    }
    Object.entries(textFilters).forEach(([key, value]) => {
      if (value) searchParams.set(key, value)
    })

    if (data.statuses.length) {
      const statusKey =
        data.statusMode === "exclude"
          ? "statusNot"
          : "status"
      searchParams.set(statusKey, data.statuses.join(","))
    }
    if (data.paymentMethods.length) {
      searchParams.set(
        "paymentMethod",
        data.paymentMethods.join(",")
      )
    }

    if (appliesPeriod) {
      // Um intervalo, um destino: o alternador escolhe QUAL data ele recorta. Mandar todos os
      // pares obrigaria a escolher uma janela por data.
      const [inicio, fim] = DATE_FIELD_PARAMS[data.dateField]

      searchParams.set(
        inicio,
        format(data.from, API_DATE_FORMAT)
      )
      searchParams.set(fim, format(data.to, API_DATE_FORMAT))
    }
    searchParams.set("refetch", String(Date.now()))

    navigate(`?${searchParams.toString()}`)
  })

  const handleResetForm = () => {
    form.reset({
      code: "",
      customerDocument: "",
      ownId: "",
      salesChannel: undefined,
      statuses: [],
      statusMode: "include",
      paymentMethods: [],
      from: startDate,
      to: endDate,
      dateField: "created",
      includePeriodWithIdentifier: false,
    })
    setSalesChannelSelected(salesChannelOptions[0])

    const searchParams = new URLSearchParams()
    const currentWhiteLabelId = new URLSearchParams(
      window.location.search
    ).get("whiteLabelId")
    if (currentWhiteLabelId) {
      searchParams.set("whiteLabelId", currentWhiteLabelId)
    }
    navigate(`?${searchParams.toString()}`)
  }

  return (
    <div className="@container">
      {/* Mobile filter toggle */}
      <button
        type="button"
        onClick={() => setIsFilterOpen((prev) => !prev)}
        className="mb-4 flex w-full items-center justify-between rounded-md border bg-background px-4 py-3 text-sm font-medium md:hidden"
      >
        <span className="flex items-center gap-2">
          <FilterIcon size={16} />
          {t("common.filters")}
          {hasFilter && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
              !
            </span>
          )}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn(
            "transition-transform duration-200",
            isFilterOpen && "rotate-180"
          )}
        />
      </button>

      <div
        className={cn(
          "md:block",
          isFilterOpen ? "block" : "hidden"
        )}
      >
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="max-w-6xl"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <FilterChip
                label={t(
                  `orders.filters.${selectedDateField}Period`
                )}
                value={dateChipValue}
                canClear={Boolean(valueOf("dateRange"))}
                onClear={() => clearFilter("dateRange")}
              >
                <DateFieldToggle
                  value={selectedDateField}
                  onChange={(field) =>
                    form.setValue("dateField", field)
                  }
                />
                <FormField
                  control={form.control}
                  name="from"
                  render={({ field }) => (
                    <FormItem className="w-full @xl:col-span-2 @5xl:col-span-1">
                      <FormControl>
                        <AppDateRangePicker
                          value={{
                            from: field.value,
                            to: selectedToDate,
                          }}
                          onChange={(range) => {
                            field.onChange(range.from)
                            form.setValue("to", range.to)
                          }}
                          disabled={
                            searchesByIdentifier &&
                            !includesPeriodWithIdentifier
                          }
                        />
                      </FormControl>
                      {searchesByIdentifier && (
                        // Buscar por identificador descarta o período — mas "os pedidos deste
                        // CPF no mês passado" precisa dos dois. Antes a tela só avisava que o
                        // período não valia; agora dá para pedir que valha.
                        <label className="flex items-start gap-2 pt-1 text-xs text-muted-foreground">
                          <Checkbox
                            checked={includesPeriodWithIdentifier}
                            onCheckedChange={(checked) =>
                              form.setValue(
                                "includePeriodWithIdentifier",
                                checked === true
                              )
                            }
                            className="mt-0.5"
                          />
                          <span>
                            {t("orders.filters.includePeriod")}
                          </span>
                        </label>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.customerDocument")}
                value={valueOf("customerDocument")}
                onClear={() => clearFilter("customerDocument")}
              >
                <FormField
                  control={form.control}
                  name="customerDocument"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Input
                          placeholder={t(
                            "orders.filters.customerDocument"
                          )}
                          maxLength={18}
                          {...field}
                          className="w-full"
                          onChange={(e) => {
                            const masked = formatDocument(
                              e.target.value
                            )
                            field.onChange(masked)
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.code")}
                value={valueOf("code")}
                onClear={() => clearFilter("code")}
              >
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Input
                          placeholder={t(
                            "orders.filters.orderCode"
                          )}
                          {...field}
                          className="w-full"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.ownId")}
                value={valueOf("ownId")}
                onClear={() => clearFilter("ownId")}
              >
                <FormField
                  control={form.control}
                  name="ownId"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Input
                          placeholder={t(
                            "orders.filters.ownId"
                          )}
                          {...field}
                          className="w-full"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.status")}
                value={valueOf("statuses")}
                onClear={() => clearFilter("statuses")}
              >
                <FormField
                  control={form.control}
                  name="statuses"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <AppMultiSelectFilter
                          options={statusOptions}
                          selected={field.value}
                          onChange={field.onChange}
                          placeholder={t(
                            "orders.filters.selectStatus"
                          )}
                          searchPlaceholder={t(
                            "orders.filters.searchStatus"
                          )}
                          emptyMessage={t(
                            "orders.filters.noStatusFound"
                          )}
                          ariaLabel={t(
                            "orders.filters.selectStatus"
                          )}
                          selectionPrefix={
                            statusMode === "exclude"
                              ? t(
                                  "orders.filters.statusModeExcludePrefix"
                                )
                              : undefined
                          }
                          header={
                            <StatusModeToggle
                              value={statusMode}
                              onChange={(mode) =>
                                form.setValue(
                                  "statusMode",
                                  mode
                                )
                              }
                            />
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.paymentMethod")}
                value={valueOf("paymentMethods")}
                onClear={() => clearFilter("paymentMethods")}
              >
                <FormField
                  control={form.control}
                  name="paymentMethods"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <AppMultiSelectFilter
                          options={paymentMethodOptions}
                          selected={field.value}
                          onChange={field.onChange}
                          placeholder={t(
                            "orders.filters.selectPaymentMethod"
                          )}
                          searchPlaceholder={t(
                            "orders.filters.searchPaymentMethod"
                          )}
                          emptyMessage={t(
                            "orders.filters.noPaymentMethodFound"
                          )}
                          ariaLabel={t(
                            "orders.filters.selectPaymentMethod"
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <FilterChip
                label={t("orders.filters.chips.salesChannel")}
                value={valueOf("salesChannel")}
                onClear={() => clearFilter("salesChannel")}
              >
                <FormField
                  control={form.control}
                  name="salesChannel"
                  render={({ field }) => (
                    <FormItem>
                      <Popover>
                        <FormControl>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="h-10 w-full justify-start"
                            >
                              {salesChannelSelected ? (
                                <>
                                  <salesChannelSelected.icon className="mr-2 h-4 w-4" />
                                  {salesChannelSelected.label}
                                </>
                              ) : (
                                <>
                                  <CircleIcon className="mr-2 h-4 w-4" />
                                  {t(
                                    "orders.filters.allChannels"
                                  )}
                                </>
                              )}
                            </Button>
                          </PopoverTrigger>
                        </FormControl>
                        <PopoverContent
                          className="w-[var(--radix-popover-trigger-width)] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput
                              placeholder={t(
                                "orders.filters.searchChannel"
                              )}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {t(
                                  "orders.filters.noChannelFound"
                                )}
                              </CommandEmpty>
                              <CommandGroup>
                                {salesChannelOptions.map(
                                  (channel) => (
                                    <CommandItem
                                      key={channel.value}
                                      value={channel.label}
                                      onSelect={() => {
                                        form.setValue(
                                          "salesChannel",
                                          channel.value ||
                                            undefined
                                        )
                                        setSalesChannelSelected(
                                          channel
                                        )
                                      }}
                                    >
                                      <channel.icon
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          channel.value ===
                                            field?.value
                                            ? "opacity-100"
                                            : "opacity-40"
                                        )}
                                      />
                                      <span>
                                        {channel.label}
                                      </span>
                                    </CommandItem>
                                  )
                                )}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  )}
                />
              </FilterChip>

              <Button
                size="sm"
                className="btn-whitelabel ml-1"
              >
                <SearchIcon size={16} className="mr-1.5" />
                {t("common.search")}
              </Button>

              {activeFilters.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResetForm}
                  className="text-muted-foreground"
                >
                  <RotateCcwIcon size={14} className="mr-1.5" />
                  {t("orders.filters.clearAll")}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}

interface DateFieldToggleProps {
  value: DateFieldFilter
  onChange: (field: DateFieldFilter) => void
}

/**
 * Escolhe A QUAL data o intervalo se aplica: criação ou pagamento.
 *
 * <p>São perguntas diferentes — o pedido criado em julho e pago em agosto entra numa e não na
 * outra. Dois seletores de período lado a lado respondiam as duas, mas obrigavam a preencher duas
 * janelas, e o segundo aparecia preenchido sem ninguém ter escolhido: parecia filtro ativo.</p>
 */
const DateFieldToggle = ({
  value,
  onChange,
}: DateFieldToggleProps) => {
  const { t } = useTranslation()
  const fields: {
    field: DateFieldFilter
    label: string
  }[] = [
    {
      field: "created",
      label: t("orders.filters.createdPeriod"),
    },
    {
      field: "paid",
      label: t("orders.filters.paidPeriod"),
    },
    {
      field: "expires",
      label: t("orders.filters.expiresPeriod"),
    },
    {
      field: "canceled",
      label: t("orders.filters.canceledPeriod"),
    },
  ]

  return (
    // Controle segmentado, e não dois botões: é UM controle com dois estados, e a barra ocupando
    // a largura toda competia com o "Buscar" — o CTA da tela. Reduzido ao conteúdo, o segmento
    // ativo pode carregar a cor da loja sem disputar atenção com o CTA.
    <div
      role="group"
      aria-label={t("orders.filters.dateFieldLabel")}
      // `max-w-full` + `flex-wrap`: com quatro opções o controle passa de 330px e, sem quebrar,
      // estouraria a coluna no celular — a página inteira ganharia rolagem horizontal.
      className="flex w-fit max-w-full flex-wrap rounded-md bg-muted p-0.5"
    >
      {fields.map(({ field, label }) => (
        <button
          key={field}
          type="button"
          aria-pressed={value === field}
          onClick={() => onChange(field)}
          className={cn(
            // O anel padrão (`ring-ring`) é da mesma família de verde do fundo `bg-muted`: fica em
            // 1.7:1 e some. Aqui ele usa o texto, que contrasta com os dois estados do segmento.
            "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-1",
            value === field
              ? "btn-whitelabel shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

interface StatusModeToggleProps {
  value: StatusFilterMode
  onChange: (mode: StatusFilterMode) => void
}

/**
 * Alternador "é / não é" do filtro de status: os status marcados podem ser o
 * recorte desejado ou justamente o que deve ficar de fora.
 */
const StatusModeToggle = ({
  value,
  onChange,
}: StatusModeToggleProps) => {
  const { t } = useTranslation()
  const modes: {
    mode: StatusFilterMode
    label: string
  }[] = [
    {
      mode: "include",
      label: t("orders.filters.statusModeInclude"),
    },
    {
      mode: "exclude",
      label: t("orders.filters.statusModeExclude"),
    },
  ]

  return (
    <div
      role="group"
      aria-label={t("orders.filters.statusModeLabel")}
      className="flex gap-1"
    >
      {modes.map(({ mode, label }) => (
        <Button
          key={mode}
          type="button"
          size="sm"
          variant={value === mode ? "default" : "ghost"}
          aria-pressed={value === mode}
          className="h-7 flex-1 text-xs"
          onClick={() => onChange(mode)}
        >
          {label}
        </Button>
      ))}
    </div>
  )
}
